// Nexora (nexora-app) — file d'attente d'écriture hors-ligne.
// Port quasi verbatim de StoreFlowProV2/public/offline-outbox.js (même contrat exact, voir
// son propre commentaire d'en-tête) — converti en module ES avec une fonction d'installation
// explicite (installOfflineOutbox()) plutôt qu'une IIFE auto-exécutée, pour être appelée en
// tout premier dans main.jsx, avant le premier rendu de App et donc avant le premier appel
// apiJSON() (client/src/lib/api.js).
//
// Règle : seul un vrai échec réseau (fetch() qui rejette — pas une réponse serveur 4xx/5xx,
// qui elle doit continuer à s'afficher comme une vraie erreur) déclenche la mise en file. Les
// envois de fichiers (FormData) ne sont jamais mis en file : hors-ligne, ils échouent
// proprement avec un message clair (couvre aussi App.jsx:2361, le seul appel qui contourne
// apiJSON — le patch agit sur window.fetch global, pas sur apiJSON spécifiquement).

const DB_NAME = "nexora-offline";
const DB_VERSION = 1;
const STORE_NAME = "outbox";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("createdAt", "createdAt");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function notifyChange() {
  window.dispatchEvent(new CustomEvent("nx-outbox-change"));
}

function makeId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `ob_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

async function serializeBody(body) {
  if (body == null) return null;
  if (typeof body === "string") return body;
  if (body instanceof Blob) return await body.text(); // rare hors FormData ; texte suffisant ici
  return body; // déjà une chaîne dans la quasi-totalité des appels de l'app (JSON.stringify côté appelant)
}

function isFileUploadBody(body) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function enqueue({ method, url, body, headers, pageContext }) {
  const entry = {
    id: makeId(),
    method,
    url,
    body: await serializeBody(body),
    headers: headers ? Object.fromEntries(new Headers(headers).entries()) : {},
    createdAt: Date.now(),
    status: "pending",
    attempts: 0,
    lastError: null,
    pageContext: pageContext || location.pathname
  };

  await withStore("readwrite", (store) => store.add(entry));
  notifyChange();
  return entry.id;
}

async function listAll() {
  return withStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result.sort((a, b) => a.createdAt - b.createdAt));
      req.onerror = () => reject(req.error);
    });
  });
}

async function remove(id) {
  await withStore("readwrite", (store) => store.delete(id));
  notifyChange();
}

async function patch(id, changes) {
  await withStore("readwrite", (store) => {
    return new Promise((resolve, reject) => {
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const current = getReq.result;
        if (!current) return resolve();
        store.put(Object.assign(current, changes));
        resolve();
      };
      getReq.onerror = () => reject(getReq.error);
    });
  });
  notifyChange();
}

function queuedResponse(outboxId) {
  return new Response(
    JSON.stringify({ queued: true, outboxId, message: "Action enregistrée hors-ligne, sera synchronisée." }),
    { status: 202, headers: { "Content-Type": "application/json" } }
  );
}

function connectionRequiredResponse() {
  return new Response(
    JSON.stringify({ error: "Cette action nécessite une connexion internet." }),
    { status: 503, headers: { "Content-Type": "application/json" } }
  );
}

// Un réseau qui vient de revenir (Wi-Fi "connecté" mais pas encore vraiment joignable) peut
// faire attendre fetch() plusieurs minutes avant d'échouer — sans limite, l'action resterait
// bloquée au lieu d'être mise en file rapidement. On force donc un délai max — cohérent avec
// le NETWORK_TIMEOUT_MS déjà utilisé côté vanilla ; apiJSON (client/src/lib/api.js) a lui
// aussi son propre timeout de 25s pour un tout autre motif (requête qui ne se règle jamais),
// géré indépendamment via son propre AbortController — les deux mécanismes ne se marchent pas
// dessus, celui-ci intercepte uniquement un rejet réel de fetch().
const NETWORK_TIMEOUT_MS = 8000;

export function installOfflineOutbox() {
  if (window.__nxOutboxInstalled) return;
  window.__nxOutboxInstalled = true;

  const originalFetch = window.fetch.bind(window);

  function fetchWithTimeout(input, init) {
    if (init && init.signal) return originalFetch(input, init); // signal déjà géré par l'appelant, ne pas interférer

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS);

    return originalFetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
  }

  // /api/login, /api/logout et tout /api/auth/* (register, reset-request/confirm,
  // verify-password, init-admin...) ne doivent JAMAIS être mis en file — contrairement à une
  // vraie écriture de données, "synchroniser une connexion plus tard" n'a aucun sens (aucune
  // session n'existe tant que le serveur n'a pas répondu pour de vrai), et l'utilisateur se
  // retrouverait bloqué avec un message "hors-ligne, sera synchronisé" au lieu d'un vrai
  // échec de connexion clair. Même règle que côté vanilla, où login.html exclut carrément le
  // chargement d'offline-outbox.js — traduite ici en exclusion par URL puisque nexora-app n'a
  // qu'une seule page (App.jsx) et patche window.fetch globalement.
  function isAuthEndpoint(url) {
    // apiJSON() (client/src/lib/api.js) ajoute toujours un paramètre anti-cache
    // ("?_=<timestamp>") — comparer le chemin seul, jamais l'URL brute avec sa query string.
    const pathname = url.split("?")[0];
    return pathname === "/api/login" || pathname === "/api/logout" || pathname.startsWith("/api/auth/");
  }

  window.fetch = async function (input, init = {}) {
    const method = String((init && init.method) || "GET").toUpperCase();
    const url = typeof input === "string" ? input : input && input.url;
    const isApiWrite = typeof url === "string" && url.startsWith("/api/") && method !== "GET" && !isAuthEndpoint(url);

    if (!isApiWrite) return originalFetch(input, init);

    try {
      return await fetchWithTimeout(input, init);
    } catch (networkError) {
      if (isFileUploadBody(init.body)) {
        return connectionRequiredResponse();
      }

      const outboxId = await enqueue({
        method,
        url,
        body: init.body,
        headers: init.headers
      });

      return queuedResponse(outboxId);
    }
  };

  window.NexoraOutbox = {
    listAll,
    remove,
    patch,
    countPending: () => listAll().then((items) => items.filter((i) => i.status !== "failed").length),
    originalFetch,
    fetchWithTimeout
  };
}
